import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { CallbackModal } from '../components/CallbackModal';
import { CallbackForm } from '../components/CallbackForm';
import { useTranslation } from '../i18n';
import { Calendar, MessageCircle, Phone, CheckCircle2, AlertCircle, Heart, FileText, Monitor, Languages, Plus, Minus, ArrowLeft } from 'lucide-react';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
// --- Localized Content ---
const content = {
  hero: {
    title: {
      en: 'Gentle paediatric dentistry in London',
      ru: 'Детская стоматология без стресса — бережный подход к детям',
      ua: 'Дитяча стоматологія без стресу — дбайливий підхід до дітей',
      ro: 'Stomatologie pediatrică blândă în Londra'
    },
    subtitle: {
      en: 'We see children for routine check-ups, toothache, decay, chipped teeth, swelling, dental trauma and other common dental problems. Our goal is to make every visit calm, supportive and stress-free for both children and parents.',
      ru: 'Мы принимаем детей с профилактическими осмотрами, кариесом, болью, сколами зубов, воспалением, травмами и другими стоматологическими проблемами. Наша цель — помочь ребёнку спокойно пройти лечение и сохранить доверие к стоматологу на будущее.',
      ua: 'Ми приймаємо дітей на профілактичні огляди, з карієсом, болем, сколами зубів, запаленням, травмами та іншими стоматологічними проблемами. Наша мета — допомогти дитині спокійно пройти лікування і зберегти довіру до стоматолога в майбутньому.',
      ro: 'Consultăm copii pentru controale de rutină, dureri de dinți, carii, dinți ciobiți, umflături, traumatisme dentare și alte probleme dentare comune. Scopul nostru este să facem fiecare vizită calmă, plină de sprijin și fără stres atât pentru copii, cât și pentru părinți.'
    },
    btnBook: {
      en: 'Book appointment',
      ru: 'Записаться на приём',
      ua: 'Записатися на прийом',
      ro: 'Programare'
    },
    btnWhatsApp: {
      en: 'WhatsApp us',
      ru: 'Написать в WhatsApp',
      ua: 'Написати в WhatsApp',
      ro: 'Scrieți-ne pe WhatsApp'
    }
  },
  intro: {
    en: 'At Dentico Clinic, we understand how important a child’s first dental experience can be. That is why we aim to make each visit gentle, clear and reassuring. We treat both baby teeth and permanent teeth in children, explain the treatment plan to parents and recommend the best option depending on the child’s age and condition.',
    ru: 'В Dentico Clinic мы понимаем, насколько важен первый опыт ребёнка у стоматолога. Поэтому мы стараемся сделать приём максимально спокойным, понятным и аккуратным. Мы работаем с молочными и постоянными зубами у детей, объясняем родителям план лечения и подбираем подходящий вариант в зависимости от возраста и ситуации.',
    ua: 'У Dentico Clinic ми розуміємо, наскільки важливий перший досвід дитини у стоматолога. Тому намагаємося зробити прийом максимально спокійним, зрозумілим і делікатним. Ми працюємо з молочними та постійними зубами у дітей, пояснюємо батькам план лікування і підбираємо оптимальний варіант залежно від віку та ситуації.',
    ro: 'La Dentico Clinic, înțelegem cât de importantă poate fi prima experiență dentară a unui copil. De aceea, ne propunem să facem fiecare vizită blândă, clară și liniștitoare. Tratăm atât dinții de lapte, cât și dinții permanenți la copii, explicăm planul de tratament părinților și recomandăm cea mai bună opțiune în funcție de vârsta și starea copilului.'
  },
  commonCasesTitle: {
    en: 'Common child dental cases',
    ru: 'Типичные случаи у детей',
    ua: 'Типові випадки у дітей',
    ro: 'Cazuri dentare comune la copii'
  },
  commonCases: [{
    title: {
      en: 'Your child has toothache',
      ru: 'У ребёнка болит зуб',
      ua: 'У дитини болить зуб',
      ro: 'Copilul dumneavoastră are dureri de dinți'
    },
    text: {
      en: 'If your child complains of pain, it is important not to delay an examination. Even a small cavity can develop quickly in children.',
      ru: 'Если ребёнок жалуется на боль, важно не откладывать осмотр. Даже небольшой кариес у детей может быстро перейти в воспаление.',
      ua: 'Якщо дитина скаржиться на біль, важливо не відкладати огляд. Навіть невеликий карієс у дітей може швидко перейти в запалення.',
      ro: 'Dacă copilul se plânge de durere, este important să nu amânați o examinare. Chiar și o carie mică se poate dezvolta rapid la copii.'
    }
  }, {
    title: {
      en: 'Decay in a baby tooth',
      ru: 'Кариес на молочном зубе',
      ua: 'Карієс на молочному зубі',
      ro: 'Carii la un dinte de lapte'
    },
    text: {
      en: 'Baby teeth should often be treated if they can be safely kept. They are important for chewing, speech and keeping space for adult teeth.',
      ru: 'Молочные зубы важно лечить, если их можно сохранить. Они участвуют в жевании, речи и формировании правильного прикуса.',
      ua: 'Молочні зуби важливо лікувати, якщо їх можна зберегти. Вони беруть участь у жуванні, мовленні та формуванні правильного прикусу.',
      ro: 'Dinții de lapte ar trebui adesea tratați dacă pot fi păstrați în siguranță. Sunt importanți pentru masticație, vorbire și păstrarea spațiului pentru dinții de adult.'
    }
  }, {
    title: {
      en: 'A chipped front tooth',
      ru: 'Откололся передний зуб',
      ua: 'Відколовся передній зуб',
      ro: 'Un dinte din față ciobit'
    },
    text: {
      en: 'After dental trauma, it is important to assess how deep the damage is and restore the tooth as early as possible, especially if it is a permanent tooth.',
      ru: 'После травмы важно оценить, насколько глубоко повреждён зуб, и восстановить его как можно раньше, особенно если это постоянный зуб.',
      ua: 'Після травми важливо оцінити, наскільки глибоко пошкоджений зуб, і відновити його якомога раніше, особливо якщо це постійний зуб.',
      ro: 'După un traumatism dentar, este important să se evalueze cât de profundă este deteriorarea și să se restaureze dintele cât mai curând posibil, mai ales dacă este un dinte permanent.'
    }
  }, {
    title: {
      en: 'Swollen gum or abscess',
      ru: 'Воспалилась десна / появился “флюс”',
      ua: 'Запалилася ясна / з’явився “флюс”',
      ro: 'Gingie umflată sau abces'
    },
    text: {
      en: 'Swelling, redness or a “gum bump” should be checked as soon as possible.',
      ru: 'Припухлость, покраснение или “шишечка” на десне у ребёнка требуют осмотра как можно быстрее.',
      ua: 'Припухлість, почервоніння або “шишечка” на яснах у дитини потребують огляду якомога швидше.',
      ro: 'Umflăturile, roșeața sau o „umflătură pe gingie” ar trebui verificate cât mai curând posibil.'
    }
  }, {
    title: {
      en: 'Loose baby tooth / extraction question',
      ru: 'Подвижный молочный зуб / вопрос об удалении',
      ua: 'Хитається молочний зуб / питання видалення',
      ro: 'Dinte de lapte mobil / întrebări despre extracție'
    },
    text: {
      en: 'Not every baby tooth needs to be removed straight away. We assess the situation and explain when it is better to save the tooth and when extraction is necessary.',
      ru: 'Не каждый молочный зуб нужно сразу удалять. Мы оцениваем ситуацию и объясняем, когда лучше сохранить зуб, а когда удаление действительно необходимо.',
      ua: 'Не кожен молочний зуб потрібно одразу видаляти. Ми оцінюємо ситуацію і пояснюємо, коли краще зберегти зуб, а коли видалення справді необхідне.',
      ro: 'Nu orice dinte de lapte trebuie extras imediat. Evaluăm situația și explicăm când este mai bine să salvăm dintele și când este necesară extracția.'
    }
  }, {
    title: {
      en: 'Harmful oral habits',
      ru: 'Неправильные привычки',
      ua: 'Неправильні звички',
      ro: 'Obiceiuri orale dăunătoare'
    },
    text: {
      en: 'Thumb sucking, prolonged dummy use, mouth breathing and similar habits can affect tooth position and bite development.',
      ru: 'Сосание пальца, длительное использование пустышки, ротовое дыхание и другие привычки могут влиять на прикус и положение зубов.',
      ua: 'Смоктання пальця, тривале використання пустушки, ротове дихання та інші звички можуть впливати на прикус і положення зубів.',
      ro: 'Sugerea degetului, utilizarea prelungită a suzetei, respirația pe gură și alte obiceiuri similare pot afecta poziția dinților și dezvoltarea mușcăturii.'
    }
  }, {
    title: {
      en: 'Tooth darkening after trauma',
      ru: 'Потемнение зуба после удара',
      ua: 'Потемніння зуба після удару',
      ro: 'Întunecarea dintelui după traumatism'
    },
    text: {
      en: 'If a tooth becomes darker after an injury, it does not always mean it has to be removed, but it does need assessment.',
      ru: 'Если зуб потемнел после травмы, это не всегда означает удаление, но обязательно требует осмотра.',
      ua: 'Якщо зуб потемнів після травми, це не завжди означає видалення, але обов’язково потребує огляду.',
      ro: 'Dacă un dinte devine mai închis la culoare după o accidentare, nu înseamnă întotdeauna că trebuie extras, dar necesită o evaluare.'
    }
  }, {
    title: {
      en: 'First dental visit',
      ru: 'Первый визит к стоматологу',
      ua: 'Перший візит до стоматолога',
      ro: 'Prima vizită la dentist'
    },
    text: {
      en: 'A preventive, pain-free first visit is the best way to introduce a child to the dentist calmly and positively.',
      ru: 'Профилактический визит без боли — лучший способ познакомить ребёнка с врачом спокойно и без страха.',
      ua: 'Профілактичний візит без болю — найкращий спосіб познайомити дитину з лікарем спокійно та без страху.',
      ro: 'O primă vizită preventivă, fără durere, este cel mai bun mod de a introduce copilul la dentist într-un mod calm și pozitiv.'
    }
  }],
  servicesTitle: {
    en: 'Our paediatric dental services',
    ru: 'Наши детские услуги',
    ua: 'Наші дитячі послуги',
    ro: 'Serviciile noastre stomatologice pediatrice'
  },
  servicesList: {
    en: ['routine check-ups', 'paediatric consultations', 'children’s fillings', 'decay treatment', 'baby tooth treatment', 'baby tooth extractions', 'care for pain and swelling', 'treatment of chipped and traumatised teeth', 'professional cleaning', 'preventive advice', 'tooth eruption monitoring', 'bite and oral habit assessment'],
    ru: ['профилактический осмотр', 'детская консультация', 'лечение кариеса у детей', 'детские пломбы', 'лечение молочных зубов', 'удаление молочных зубов', 'помощь при боли и воспалении', 'лечение сколов и травм зубов', 'профессиональная гигиена', 'рекомендации по уходу и профилактике', 'контроль прорезывания зубов', 'оценка прикуса и стоматологических привычек'],
    ua: ['профілактичний огляд', 'дитяча консультація', 'лікування карієсу у дітей', 'дитячі пломби', 'лікування молочних зубів', 'видалення молочних зубів', 'допомога при болю та запаленні', 'лікування сколів і травм зубів', 'професійна гігієна', 'рекомендації з догляду та профілактики', 'контроль прорізування зубів', 'оцінка прикусу та стоматологічних звичок'],
    ro: ['controale de rutină', 'consultații pediatrice', 'plombe pentru copii', 'tratamentul cariilor', 'tratamentul dinților de lapte', 'extracții de dinți de lapte', 'îngrijire pentru durere și umflături', 'tratamentul dinților ciobiți și traumatizați', 'curățare profesională', 'sfaturi preventive', 'monitorizarea erupției dentare', 'evaluarea mușcăturii și a obiceiurilor orale']
  },
  urgentTitle: {
    en: 'When to seek urgent help',
    ru: 'Когда нужно срочно обратиться',
    ua: 'Коли потрібно звернутися терміново',
    ro: 'Când să solicitați ajutor urgent'
  },
  urgentSubtitle: {
    en: 'Please contact us as soon as possible if your child has:',
    ru: 'Обратитесь как можно скорее, если у ребёнка:',
    ua: 'Зверніться якнайшвидше, якщо у дитини:',
    ro: 'Vă rugăm să ne contactați cât mai curând posibil dacă copilul dumneavoastră are:'
  },
  urgentList: {
    en: ['severe toothache', 'swelling of the gum or face', 'fever with dental pain', 'a chipped or traumatised permanent tooth', 'bleeding after a fall or injury', 'difficulty eating or opening the mouth'],
    ru: ['сильная зубная боль', 'припухлость десны или щеки', 'температура на фоне зубной боли', 'скол или травма постоянного зуба', 'кровотечение после удара', 'ребёнок не может нормально жевать или открывать рот'],
    ua: ['сильний зубний біль', 'припухлість ясен або щоки', 'температура на фоні зубного болю', 'скол або травма постійного зуба', 'кровотеча після удару', 'дитині важко жувати або відкривати рот'],
    ro: ['durere de dinți severă', 'umflarea gingiei sau a feței', 'febră cu durere dentară', 'un dinte permanent ciobit sau traumatizat', 'sângerare după o cădere sau accidentare', 'dificultate la mâncare sau la deschiderea gurii']
  },
  whyChooseUsTitle: {
    en: 'Why parents choose us',
    ru: 'Почему родители выбирают нас',
    ua: 'Чому батьки обирають нас',
    ro: 'De ce ne aleg părinții'
  },
  whyChooseUs: [{
    title: {
      en: 'Gentle approach',
      ru: 'Бережный подход',
      ua: 'Дбайливий підхід',
      ro: 'Abordare blândă'
    },
    text: {
      en: 'We aim to make dental visits calm and child-friendly.',
      ru: 'Мы стараемся сделать приём спокойным и понятным для ребёнка.',
      ua: 'Ми намагаємося зробити прийом спокійним і зрозумілим для дитини.',
      ro: 'Ne propunem să facem vizitele la dentist calme și prietenoase pentru copii.'
    },
    icon: Heart
  }, {
    title: {
      en: 'Clear treatment plan',
      ru: 'Понятный план лечения',
      ua: 'Зрозумілий план лікування',
      ro: 'Plan de tratament clar'
    },
    text: {
      en: 'Parents understand what is happening, why treatment is needed and what the options are.',
      ru: 'Родители знают, что происходит, зачем это нужно и какие есть варианты.',
      ua: 'Батьки знають, що відбувається, навіщо це потрібно і які є варіанти.',
      ro: 'Părinții înțeleg ce se întâmplă, de ce este nevoie de tratament și care sunt opțiunile.'
    },
    icon: FileText
  }, {
    title: {
      en: 'Modern diagnostics',
      ru: 'Современная диагностика',
      ua: 'Сучасна діагностика',
      ro: 'Diagnosticare modernă'
    },
    text: {
      en: 'We use X-rays when needed and take a modern digital approach to treatment planning.',
      ru: 'При необходимости используем рентген и цифровой подход к планированию лечения.',
      ua: 'За потреби використовуємо рентген і цифровий підхід до планування лікування.',
      ro: 'Folosim radiografii atunci când este necesar și adoptăm o abordare digitală modernă pentru planificarea tratamentului.'
    },
    icon: Monitor
  }, {
    title: {
      en: 'Language support',
      ru: 'Поддержка на вашем языке',
      ua: 'Підтримка вашою мовою',
      ro: 'Suport lingvistic'
    },
    text: {
      en: 'We speak English, Ukrainian and Russian.',
      ru: 'Мы говорим на русском, украинском и английском.',
      ua: 'Ми говоримо українською, російською та англійською.',
      ro: 'Vorbim engleză, ucraineană și rusă.'
    },
    icon: Languages
  }],
  faqTitle: {
    en: 'FAQ',
    ru: 'FAQ',
    ua: 'FAQ',
    ro: 'Întrebări frecvente'
  },
  faqs: [{
    q: {
      en: 'Should baby teeth be treated?',
      ru: 'Нужно ли лечить молочные зубы?',
      ua: 'Чи потрібно лікувати молочні зубы?',
      ro: 'Ar trebui tratați dinții de lapte?'
    },
    a: {
      en: 'Yes, in many cases baby teeth should be treated if they can be safely preserved.',
      ru: 'Да, если зуб можно сохранить и он ещё должен служить ребёнку. Молочные зубы важны для жевания, речи и правильного прорезывания постоянных зубов.',
      ua: 'Так, якщо зуб можна зберегти і він ще має служити дитині. Молочні зуби важливі для жування, мовлення та правильного прорізування постійних зубів.',
      ro: 'Da, în multe cazuri dinții de lapte ar trebui tratați dacă pot fi păstrați în siguranță.'
    }
  }, {
    q: {
      en: 'What should I do if my child chips a tooth?',
      ru: 'Что делать, если ребёнок ударился и отколол зуб?',
      ua: 'Що робити, якщо дитина вдарилася і відколола зуб?',
      ro: 'Ce ar trebui să fac dacă copilul meu își ciobește un dinte?'
    },
    a: {
      en: 'Please arrange an examination as soon as possible, especially if it is a permanent tooth.',
      ru: 'Лучше как можно быстрее записаться на осмотр. Если это постоянный зуб, важно не откладывать.',
      ua: 'Краще якнайшвидше записатися на огляд. Якщо це постійний зуб, відкладати не варто.',
      ro: 'Vă rugăm să programați o examinare cât mai curând posibil, mai ales dacă este un dinte permanent.'
    }
  }, {
    q: {
      en: 'Can we come if my child is afraid of the dentist?',
      ru: 'Можно ли прийти, если ребёнок боится стоматолога?',
      ua: 'Чи можна прийти, якщо дитина боїться стоматолога?',
      ro: 'Putem veni dacă copilului meu îi este frică de dentist?'
    },
    a: {
      en: 'Yes. We understand this is common and we do our best to make the visit as calm as possible.',
      ru: 'Да. Мы понимаем, что многие дети тревожатся, и стараемся сделать визит максимально спокойным.',
      ua: 'Так. Ми розуміємо, що багато дітей тривожаться, і намагаємося зробити візит максимально спокійним.',
      ro: 'Da. Înțelegem că acest lucru este comun și facem tot posibilul pentru a face vizita cât mai calmă posibil.'
    }
  }, {
    q: {
      en: 'When should a child first see a dentist?',
      ru: 'Когда вести ребёнка на первый осмотр?',
      ua: 'Коли приводити дитину на перший огляд?',
      ro: 'Când ar trebui un copil să vadă prima dată un dentist?'
    },
    a: {
      en: 'Ideally before there is any pain, as a preventive visit.',
      ru: 'Лучше не ждать боли. Первый визит желательно делать профилактическим.',
      ua: 'Краще не чекати болю. Перший візит бажано робити профілактичним.',
      ro: 'În mod ideal, înainte de a exista orice durere, ca o vizită preventivă.'
    }
  }],
  ctaTitle: {
    en: 'Do you have questions about your child’s teeth?',
    ru: 'Есть вопросы по детским зубам?',
    ua: 'Є питання щодо дитячих зубів?',
    ro: 'Aveți întrebări despre dinții copilului dumneavoastră?'
  },
  ctaText: {
    en: 'If your child has toothache, swelling, a chipped tooth or if you would like a routine check-up, book a consultation with us.',
    ru: 'Если у ребёнка болит зуб, откололся зуб, появилась припухлость или вы просто хотите профилактический осмотр — запишитесь на консультацию.',
    ua: 'Якщо у дитини болит зуб, відколовся зуб, з’явилася припухлість або ви просто хочете профілактичний огляд — запишіться на консультацію.',
    ro: 'Dacă copilul dumneavoastră are dureri de dinți, umflături, un dinte ciobit sau dacă doriți un control de rutină, programați o consultație la noi.'
  },
  btnContact: {
    en: 'Contact us',
    ru: 'Связаться с нами',
    ua: 'Зв’язатися з нами',
    ro: 'Contactați-ne'
  }
};
function FaqItem({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <div className="border-b border-gray-100 last:border-b-0" data-id="element-2830" data-mp="3obsmbpo204vi">
      <button onClick={onToggle} className="w-full flex items-center justify-between py-4 text-left focus:outline-none group" data-id="element-2831" data-mp="3occ1wzq8eh6d">
        <span className="text-base font-medium text-gray-900 group-hover:text-forest-600 transition-colors" data-id="element-2832" data-mp="jy3wc0ovj9wv">
          {question}
        </span>
        <span className={`flex-shrink-0 ml-4 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-forest-600 text-white' : 'bg-gray-100 text-gray-400'}`} data-id="element-2833" data-mp="65nyrfeqs43c">
          {isOpen ? <Minus className="w-4 h-4" data-id="element-2834" data-mp="13ll31sqjayjt" /> : <Plus className="w-4 h-4" data-id="element-2835" data-mp="13mcwoy0kmm7g" />}
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'}`} data-id="element-2836" data-mp="1czll2pz13b9w">
        <p className="text-gray-600 text-sm leading-relaxed" data-id="element-2837" data-mp="18wyuese3fwea">{answer}</p>
      </div>
    </div>;
}
export function PaediatricDentistryPage() {
  const {
    lang,
    t
  } = useTranslation();
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const seoTitle = lang === 'en' ? 'Paediatric Dentist in Angel, London | Dentico Clinic' : lang === 'ua' ? 'Дитячий стоматолог в Angel, Лондон | Dentico Clinic' : lang === 'ro' ? 'Dentist Pediatru în Angel, Londra | Dentico Clinic' : 'Детский стоматолог в Angel, Лондон | Dentico Clinic';
  const seoDescription = lang === 'en' ? 'Gentle paediatric dentistry in Angel, London for check-ups, toothache, decay, dental trauma and preventive care. Book at Dentico Clinic.' : lang === 'ua' ? 'Дбайлива дитяча стоматологія в Angel, Лондон: огляди, зубний біль, карієс, травми зубів і профілактика. Запис у Dentico Clinic.' : lang === 'ro' ? 'Stomatologie pediatrică blândă în Angel, Londra pentru controale, dureri de dinți, carii, traumatisme dentare și îngrijire preventivă. Programați-vă la Dentico Clinic.' : 'Бережная детская стоматология в Angel, Лондон: осмотры, зубная боль, кариес, травмы зубов и профилактика. Запись в Dentico Clinic.';
  return <Layout title={seoTitle} description={seoDescription} data-id="element-2838" data-mp="23s3x2qmojibx">
      <CallbackModal isOpen={isCallbackOpen} onClose={() => setIsCallbackOpen(false)} data-id="element-2839" data-mp="28q5frag71fmv" />

      {/* Hero Section */}
      <div className="relative" data-id="element-2840" data-mp="2diyk3x7r2lyf">
        <div className="h-[400px] sm:h-[480px] lg:h-[540px] w-full overflow-hidden" data-id="element-2841" data-mp="1g1zrwetufait">
          <img src="https://images.unsplash.com/photo-1519689680058-324335c77eba?auto=format&fit=crop&w=1200&q=80" alt={content.hero.title[lang]} className="w-full h-full object-cover" data-id="element-2842" data-mp="1g05gps8diwrx" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" data-id="element-2843" data-mp="18hxkc90g1un8" />
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 lg:p-12" data-id="element-2844" data-mp="13icjtu5mle8e">
          <div className="max-w-7xl mx-auto" data-id="element-2845" data-mp="yksthvvnlb03">
            <Link to={`/${lang}/services`} className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors mb-4 text-sm font-medium" data-id="element-2846" data-mp="yk197h7iazp4">
              <ArrowLeft className="w-4 h-4" data-id="element-2847" data-mp="pqo9nwexszu2" />
              {t('services.viewAll')}
            </Link>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 max-w-3xl" data-id="element-2848" data-mp="kt60c4x0cilj">
              {content.hero.title[lang]}
            </h1>
            <p className="text-white/90 text-lg max-w-2xl mb-8 leading-relaxed" data-id="element-2849" data-mp="auyizkyt3yj0">
              {content.hero.subtitle[lang]}
            </p>
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-2850" data-mp="6twpzlvhgiph">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-id="element-2851" data-mp="vksn43b04wcg">
                <Button variant="primary" surface="dark" size="lg" className="gap-2 w-full sm:w-auto" data-id="element-2852" data-mp="10jdpo9dq7s42">
                  <Calendar className="w-5 h-5" data-id="element-2853" data-mp="qssuyx16b7qf" />
                  {content.hero.btnBook[lang]}
                </Button>
              </a>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-id="element-2854" data-mp="csi43tzv5t59">
                <Button variant="secondary" surface="dark" size="lg" className="gap-2 w-full sm:w-auto" data-id="element-2855" data-mp="hsmh3a1kc5qq">
                  <MessageCircle className="w-5 h-5" data-id="element-2856" data-mp="7tewq2wevau4" />
                  {content.hero.btnWhatsApp[lang]}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Intro Section */}
      <section className="py-16 lg:py-20 bg-white" data-id="element-2857" data-mp="227o8gfo3335e">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2858" data-mp="3mpqnb4riuw7i">
          <p className="text-lg sm:text-xl text-gray-600 leading-relaxed" data-id="element-2859" data-mp="3ion88khat47c">
            {content.intro[lang]}
          </p>
        </div>
      </section>

      {/* Common Cases */}
      <section className="py-16 lg:py-20 bg-gray-50" data-id="element-2860" data-mp="cqqr33uq04x">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2861" data-mp="3rp371gn3jn5v">
          <div className="text-center mb-12" data-id="element-2862" data-mp="a8trq5myvsqa">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-2863" data-mp="a7ioc6xfomzp">
              {content.commonCasesTitle[lang]}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2864" data-mp="5c3fjtea9ha5">
            {content.commonCases.map((caseItem, idx) => <div key={idx} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow" data-id="element-2865" data-mp="e9x42i5t36mr" data-mpi={idx}>
                <h3 className="text-lg font-bold text-forest-700 mb-3" data-id="element-2866" data-mp="2g21tnmzu1rub">
                  {caseItem.title[lang]}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed" data-id="element-2867" data-mp="jzp0jpxpfne9">
                  {caseItem.text[lang]}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Services & Urgent Help */}
      <section className="py-16 lg:py-20 bg-white" data-id="element-2868" data-mp="tz7jq45sj559">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2869" data-mp="xzhlf4mjv9eb">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start" data-id="element-2870" data-mp="12xswfiokzpy4">
            {/* Services List */}
            <div data-id="element-2871" data-mp="17w2r7s6e8oj7">
              <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-6" data-id="element-2872" data-mp="17vkqojcp7o5h">
                {content.servicesTitle[lang]}
              </h2>
              <div className="space-y-3" data-id="element-2873" data-mp="1cpowv0e53s5i">
                {content.servicesList[lang].map((service, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-2874" data-mp="1lo0yrykgicd9" data-mpi={idx}>
                    <CheckCircle2 className="w-5 h-5 text-forest-600 flex-shrink-0 mt-0.5" data-id="element-2875" data-mp="1lpu1j2ry8cfz" />
                    <span className="text-gray-700 font-medium capitalize" data-id="element-2876" data-mp="1qndiiag173bm">
                      {service}
                    </span>
                  </div>)}
              </div>
            </div>

            {/* Urgent Help */}
            <div className="bg-red-50 border border-red-100 rounded-3xl p-8" data-id="element-2877" data-mp="3246q8xz2dfuy">
              <div className="flex items-center gap-3 mb-4" data-id="element-2878" data-mp="326ay1r032z0i">
                <AlertCircle className="w-8 h-8 text-red-600" data-id="element-2879" data-mp="2x6y3ek2z0nnd" />
                <h2 className="text-2xl font-bold text-red-900" data-id="element-2880" data-mp="3tw5pbt7i4yrt">
                  {content.urgentTitle[lang]}
                </h2>
              </div>
              <p className="text-red-800 font-medium mb-6" data-id="element-2881" data-mp="3puu6c6j4vbsl">
                {content.urgentSubtitle[lang]}
              </p>
              <ul className="space-y-3 mb-8" data-id="element-2882" data-mp="3g48zxwx7x2z4">
                {content.urgentList[lang].map((item, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-2883" data-mp="hdz6z11jo9j9" data-mpi={idx}>
                    <div className="w-1.5 h-1.5 rounded-full bg-red-500 mt-2 flex-shrink-0" data-id="element-2884" data-mp="hgbwbz9mtm6c" />
                    <span className="text-red-900" data-id="element-2885" data-mp="clfq9v7k8ya8">{item}</span>
                  </li>)}
              </ul>
              <div className="flex flex-col sm:flex-row gap-3" data-id="element-2886" data-mp="3lw21qftbs2qa">
                <a href="tel:+447345197820" className="w-full" data-id="element-2887" data-mp="3lxd4ccb1b4ft">
                  <Button className="w-full bg-red-600 hover:bg-red-700 text-white gap-2" size="lg" data-id="element-2888" data-mp="377xhh7emjj5e">
                    <Phone className="w-5 h-5" data-id="element-2889" data-mp="3c3wdnh6725gd" />
                    Call Now
                  </Button>
                </a>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full" data-id="element-2890" data-mp="8q1yk6o1lgtm">
                  <Button variant="outline" className="w-full border-red-200 text-red-700 hover:bg-red-100 gap-2" size="lg" data-id="element-2891" data-mp="3uwgprmxobvxh">
                    <MessageCircle className="w-5 h-5" data-id="element-2892" data-mp="2ji33gmikjojk" />
                    WhatsApp
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 lg:py-20 bg-forest-900 text-white" data-id="element-2893" data-mp="26yfwy6a020b9">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2894" data-mp="2lwi2gm0lcvv5">
          <div className="text-center mb-12" data-id="element-2895" data-mp="2ltvs6embg298">
            <h2 className="text-3xl font-bold mb-4" data-id="element-2896" data-mp="2gy6t0ce7kndg">
              {content.whyChooseUsTitle[lang]}
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-2897" data-mp="1t7ueujsdduef">
            {content.whyChooseUs.map((reason, idx) => {
            const Icon = reason.icon;
            return <div key={idx} className="text-center" data-id="element-2898" data-mp="22yoxle6eqbv4" data-mpi={idx}>
                  <div className="w-14 h-14 bg-[#3A5A4D] rounded-2xl flex items-center justify-center mx-auto mb-4 text-white" data-id="element-2899" data-mp="1y6dyuenekw45">
                    <Icon className="w-7 h-7" data-id="element-2900" data-mp="1afibbtpzcn2p" />
                  </div>
                  <h3 className="text-lg font-bold mb-2" data-id="element-2901" data-mp="15irx7bxqk1jx">
                    {reason.title[lang]}
                  </h3>
                  <p className="text-ivory/80 text-sm leading-relaxed" data-id="element-2902" data-mp="4z9braopo1gr">
                    {reason.text[lang]}
                  </p>
                </div>;
          })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-20 bg-gray-50" data-id="element-2903" data-mp="3mezc9duhlnmj">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2904" data-mp="3mgacj3oq4d8w">
          <div className="text-center mb-10" data-id="element-2905" data-mp="nxvd8qo53ul7">
            <h2 className="text-3xl font-bold text-gray-900" data-id="element-2906" data-mp="nwkamu6fksvo">
              {content.faqTitle[lang]}
            </h2>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-2 sm:p-4" data-id="element-2907" data-mp="dz5u6ukic43d">
            {content.faqs.map((faq, idx) => <FaqItem key={idx} question={faq.q[lang]} answer={faq.a[lang]} isOpen={openFaqIndex === idx} onToggle={() => setOpenFaqIndex(openFaqIndex === idx ? null : idx)} data-id="element-2908" data-mp="8z1g1b72nlgb" data-mpi={idx} />)}
          </div>
        </div>
      </section>

      {/* Final CTA & Callback Form */}
      <section className="py-16 lg:py-20 bg-white" data-id="element-2909" data-mp="1e848vdcraozu">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2910" data-mp="1j6fe1as3wbbq">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-2911" data-mp="1j54exo9lvrqy">
            <div data-id="element-2912" data-mp="1nxxhc5hlqnzv">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6" data-id="element-2913" data-mp="1nygr2yx3m62t">
                {content.ctaTitle[lang]}
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-2914" data-mp="1wxka62w9mlhb">
                {content.ctaText[lang]}
              </p>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-2915" data-mp="21xyg9feb3lcp">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-id="element-2916" data-mp="26vrmg8fm0fcz">
                  <Button size="lg" className="gap-2 w-full sm:w-auto !opacity-100 !bg-[#2F4A3F] !text-white !border !border-[#2F4A3F] hover:!bg-[#3A5A4D]" data-id="element-2917" data-mp="26tnixrlmhid8">
                    <Calendar className="w-5 h-5" data-id="element-2918" data-mp="4h14zife38j4" />
                    {content.hero.btnBook[lang]}
                  </Button>
                </a>
                <Button size="lg" className="gap-2 w-full sm:w-auto !opacity-100 !bg-transparent !text-[#2F4A3F] !border !border-[#2F4A3F] hover:!bg-[#F8F5F0]" onClick={() => setIsCallbackOpen(true)} data-id="element-2919" data-mp="ha8qz73g077">
                  <Phone className="w-5 h-5 text-[#2F4A3F]" data-id="element-2920" data-mp="nfdh9d1lkyix" />
                  {content.btnContact[lang]}
                </Button>
              </div>
            </div>
            <div className="bg-gray-50 p-6 sm:p-8 rounded-3xl border border-gray-100" data-id="element-2921" data-mp="x5ynnmnij7ce">
              <CallbackForm data-id="element-2922" data-mp="x7sy2715rh28" />
            </div>
          </div>
        </div>
      </section>
    </Layout>;
}